// src/components/Admin/Leads/TableLeads.js
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { DateRangeFilter } from "../../common/DateRangeFilter";
import {
  LEADS_TABLE_DATE_FILTER_STORAGE_KEY,
  buildCreatedAtParams,
} from "../../../utils/dateRanges";

const STORAGE_KEY = "crm.leadsTable.filters";

function loadFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      status: parsed?.status ?? "",
      stage: parsed?.stage ?? "",
      source: parsed?.source ?? "",
      search: parsed?.search ?? "",
      ordering: parsed?.ordering ?? "-created_at",
      // paginación
      page: Number.isFinite(parsed?.page) ? parsed.page : 0,
      rowsPerPage: Number.isFinite(parsed?.rowsPerPage) ? parsed.rowsPerPage : 10,
    };
  } catch {
    return null;
  }
}

function saveFilters(next) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "open", label: "Abierto" },
  { value: "won", label: "Ganado" },
  { value: "lost", label: "Perdido" },
  { value: "archived", label: "Archivado" },
];

const ORDERING_OPTIONS = [
  { value: "-created_at", label: "Más recientes" },
  { value: "created_at", label: "Más antiguos" },
  { value: "-estimated_value", label: "Mayor valor" },
  { value: "estimated_value", label: "Menor valor" },
];

export function TableLeads({
  leads,
  stages = [],
  sources = [],
  onEdit,
  onDelete,
  onFiltersChange,
}) {
  const persisted = useMemo(() => loadFilters(), []);

  const [status, setStatus] = useState(persisted?.status ?? "");
  const [stage, setStage] = useState(persisted?.stage ?? "");
  const [source, setSource] = useState(persisted?.source ?? "");
  const [search, setSearch] = useState(persisted?.search ?? "");
  const [ordering, setOrdering] = useState(persisted?.ordering ?? "-created_at");

  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });

  // ✅ paginación (solo UI, NO dispara fetch)
  const [page, setPage] = useState(persisted?.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(persisted?.rowsPerPage ?? 10);

  // Persist filtros (incluye paginación)
  useEffect(() => {
    saveFilters({ status, stage, source, search, ordering, page, rowsPerPage });
  }, [status, stage, source, search, ordering, page, rowsPerPage]);

  const emit = (overrides = {}) => {
    const params = {
      ...(search ? { search } : {}),
      ...(ordering ? { ordering } : {}),
      ...(status ? { status } : {}),
      ...(stage ? { stage } : {}),
      ...(source ? { source } : {}),
      ...buildCreatedAtParams(dateRange.startDate, dateRange.endDate),
      ...overrides,
    };
    onFiltersChange?.(params);
  };

  // ✅ cuando cambian filtros que afectan data, vuelve a la página 0
  const resetPage = () => setPage(0);

  // ✅ si por un filtro quedas en una página que ya no existe, corrige
  useEffect(() => {
    const total = (leads || []).length;
    const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [leads, page, rowsPerPage]);

  const paginatedLeads = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return (leads || []).slice(start, end);
  }, [leads, page, rowsPerPage]);

  return (
    <Box>
      {/* Filtros */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <DateRangeFilter
          storageKey={LEADS_TABLE_DATE_FILTER_STORAGE_KEY}
          defaultRangeKey="last7"
          onChange={({ startDate, endDate }) => {
            setDateRange({ startDate, endDate });
            resetPage();
            onFiltersChange?.({
              ...(search ? { search } : {}),
              ...(ordering ? { ordering } : {}),
              ...(status ? { status } : {}),
              ...(stage ? { stage } : {}),
              ...(source ? { source } : {}),
              ...buildCreatedAtParams(startDate, endDate),
            });
          }}
        />

        <Select
          size="small"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            resetPage();
            emit({ status: e.target.value || undefined });
          }}
          displayEmpty
        >
          {STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            resetPage();
            emit({ stage: e.target.value || undefined });
          }}
          displayEmpty
        >
          <MenuItem value="">Todas las etapas</MenuItem>
          {stages.map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>
              {s.name}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            resetPage();
            emit({ source: e.target.value || undefined });
          }}
          displayEmpty
        >
          <MenuItem value="">Todas las fuentes</MenuItem>
          {sources.map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>
              {s.name}
            </MenuItem>
          ))}
        </Select>

        <TextField
          size="small"
          label="Buscar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              resetPage();
              emit();
            }
          }}
        />

        <Select
          size="small"
          value={ordering}
          onChange={(e) => {
            setOrdering(e.target.value);
            resetPage();
            emit({ ordering: e.target.value });
          }}
        >
          {ORDERING_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>

        <Button
          variant="outlined"
          onClick={() => {
            setStatus("");
            setStage("");
            setSource("");
            setSearch("");
            setOrdering("-created_at");
            resetPage();
            onFiltersChange?.({
              ordering: "-created_at",
              ...buildCreatedAtParams(dateRange.startDate, dateRange.endDate),
            });
          }}
        >
          Limpiar filtros
        </Button>
      </Box>

      {/* Tabla */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cliente</TableCell>
            <TableCell>Empresa</TableCell>
            <TableCell>Etapa</TableCell>
            <TableCell>Fuente</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell>Creado</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedLeads.map((l) => (
            <TableRow key={l.id} hover>
              <TableCell>{l.title}</TableCell>
              <TableCell>{l.company_name || "-"}</TableCell>
              <TableCell>{l.stage?.name ?? "-"}</TableCell>
              <TableCell>{l.source?.name ?? "-"}</TableCell>
              <TableCell>{l.status}</TableCell>
              <TableCell align="right">{Number(l.estimated_value || 0).toFixed(2)}</TableCell>
              <TableCell>{l.created_at ? dayjs(l.created_at).format("DD/MM/YYYY") : "-"}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => onEdit?.(l)} title="Editar">
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete?.(l)} title="Eliminar">
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* ✅ Paginación */}
      <TablePagination
        component="div"
        count={(leads || []).length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          const next = parseInt(e.target.value, 10);
          setRowsPerPage(next);
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Filas"
      />
    </Box>
  );
}
